<template>
  <Loader :value="promises.fetchOne" class="residential-template-mutate view" :message="$metaInfo.title">
    <div class="container">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #right>
          <div class="row wrap g-1">
            <Actions placement="residential-template" :query="{ residentialTemplate: id }">
              <template #bottom>
                <template v-if="!isNew">
                  <ButtonComponent
                    v-if="item.hasReferences"
                    :label="$t('residentialTemplateSyncReferences')"
                    @click="syncDialog = true"
                    :icon="['fal', 'sync-alt']"
                  />
                  <ButtonComponent
                    :label="$t('clone')"
                    :promise="duplicatePromise"
                    @click="duplicate"
                    :icon="['fal', 'copy']"
                  />
                  <ButtonComponent :label="$t('delete')" @click="removeDialog = true" :icon="['fal', 'times']" />
                </template>
              </template>
            </Actions>

            <ButtonComponent
              :label="saveState.label"
              :success-label="$t('saved')"
              :loading-label="$t('saving')"
              :icon="['fal', saveState.icon]"
              theme="primary"
              :promise="promises.mutate"
              :disabled="!validator.passes"
              :class="{
                'elder-button--disabled': saveState.disabled,
                'elder-button--invalid': saveState.invalid,
              }"
              v-tooltip="{ content: saveState.tooltip }"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>
      <StepperComponent :value="steps" ref="stepper" state-handler="route" @submit="submit">
        <template #after="{ meta, next, prev }">
          <div class="row center g-1">
            <ButtonComponent
              v-if="meta.prev"
              :label="$t('prev')"
              :type="isNew ? 'button' : 'submit'"
              :icon="['fal', 'angle-left']"
              icon-placement="left"
              @click="onNavigation(prev)"
            />
            <ButtonComponent
              v-if="meta.next"
              :label="$t('next')"
              :type="isNew ? 'button' : 'submit'"
              :icon="['fal', 'angle-right']"
              @click="onNavigation(next)"
            />
            <ButtonComponent
              v-if="!meta.next"
              :label="$t('complete')"
              :success-label="$t('completed')"
              :loading-label="$t('saving')"
              :icon="['fal', 'arrow-to-right']"
              theme="success"
              type="button"
              :disabled="!validator.passes"
              :promise="promises.mutate"
              @click="complete"
            />
          </div>
        </template>
      </StepperComponent>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: 'residential-templates',
          })
        "
        :resource="$tc('unitCatalog')"
      />
      <SyncComponent v-if="syncDialog" :id="id" @close="syncDialog = false" />
    </div>
  </Loader>
</template>

<script>
import Model from '@/models/ResidentialTemplate/base'
import ModelMixin from '@/mixins/Model'
import ValidatorMixin from '@/mixins/validator-v2'
import { getValidatorConfig } from '@kvass/custom-fields'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import Api from '@/api/ResidentialTemplate'
import fields from '@/components/Residential/StepsFields/fields.js'
import defaultFields from '@/components/Residential/StepsFields/defaults.js'
import { getFieldsValues, setFieldsValue } from '@/components/Residential/StepsFields/utils.js'
import { buildStepsFields, setByPath, mergeAllDeepRight } from '@/utils'

import Steps from './Steps'
import { StepperComponent } from 'vue-elder-stepper'
import { DropdownComponent } from 'vue-elder-dropdown'
import RemoveComponent from '@/components/Remove'
import HeaderComponent from '@/components/Header'
import SyncComponent from '@/components/ResidentialTemplate/Sync'
import Actions from '@/components/Actions'

export default {
  mixins: [
    ValidatorMixin({
      rules: 'rules',
      labels: 'labels',
      data: 'internalData',
    }),
    ModelMixin({
      source: 'item',
      guard: true,
      model: Model,
      transform: {
        'customFieldsConfig.*.schema': undefined,
        properties: arr =>
          arr.map((a, index) => {
            if (a.value) a.value = a.value.toString()
            a.order = index + 1
            return a
          }),
      },
    }),
  ],
  props: {
    id: String,
  },
  data() {
    return {
      removeDialog: false,
      syncDialog: false,
      duplicatePromise: null,
      internalNameState: false,
      internalData: {},
      view: 'residential-template',
    }
  },
  watch: {
    id: {
      handler(id) {
        if (this.isNew) {
          this.set()
          this.internalData = this.valueComp
          return
        }

        return this.fetch({ id }).then(() => {
          this.internalData = this.valueComp
          if (!this.item.id) return this.$router.replace({ name: 'notfound' })
        })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('ResidentialTemplate', ['item', 'promises']),

    labels() {
      return {
        ...this.fieldValidatorConfig.labels,
      }
    },
    rules() {
      return {
        ...this.fieldValidatorConfig.rules,
      }
    },

    saveState() {
      let base = {
        label: this.$t('save'),
        icon: 'arrow-to-bottom',
        disabled: !this.validator.passes || !this.hasChanges,
      }

      if (!this.validator.passes)
        return {
          ...base,
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('\n'),
        }

      if (!this.hasChanges)
        return {
          ...base,
          tooltip: this.$t('noUnsavedChanges'),
        }

      return base
    },
    isNew() {
      return !this.id
    },
    breadcrumb() {
      return [
        { title: this.$tc('unitCatalog', 1), action: { name: 'residential-templates' } },
        { title: this.$metaInfo.title },
      ]
    },
    fieldValidatorConfig() {
      if (!this.schemas.length) return {}
      return getValidatorConfig([...this.schemas], this.data, { prefix: '' })
    },
    schemas() {
      return [
        ...[...Array.from(defaultFields.values() || []).map(i => i(this))].filter(i => {
          const placement = i.placement instanceof Array ? i.placement : [i.placement]
          return placement.map(p => p.split(':').shift()).includes('residential-template')
        }),
        ...[...(this.$path('data.customFieldsConfig') || [])]
          .map(c =>
            c.schema.map(e => ({ ...e, placement: 'residential-template:presentation', dataKey: 'customFields' })),
          )
          .flat(),
      ]
    },
    valueComp: {
      get() {
        //map data to fields
        return {
          ...getFieldsValues(this.schemas, {
            ...this.data,
            showInternalName: this.showInternalName,
          }),
          ...this.data,
          ...(this.$path('customFields', this.data) || {}),
          view: this.view,
        }
      },
      set(newVal) {
        //if slot -> update single field
        if (newVal.slot) {
          const { key, value } = newVal
          return setByPath(key, value, this.data)
        }

        const fieldsData = setFieldsValue(newVal, this.schemas)
        //not slot -> customFields object is newVal -> Tranform this to fit merge with data
        this.data = mergeAllDeepRight(this.data, fieldsData)
        this.showInternalName = fieldsData.showInternalName

        this.internalData = newVal
      },
    },
    steps() {
      let props = {
        value: this.valueComp,
        labels: this.labels,
        validator: this.validator,
        promises: this.promises,
        id: this.data.id,
        schemas: this.schemas,
        fields: buildStepsFields(Array.from(fields.values()), this.schemas, this),
      }

      let listeners = {
        submit: () => this.submit(),
        updateValue: e => (this.valueComp = e),
      }

      return Steps().map(raw => {
        const { icon, ...s } = raw
        s.props = {
          ...(s.props || {}),
          ...props,
        }
        s.listeners = {
          ...(s.listeners || {}),
          ...listeners,
        }
        s.disabled = raw.id == 'shop' && this.isNew
        return s
      })
    },
    showInternalName: {
      get() {
        if (!this.data) return
        return this.internalNameState || (this.data.internalName && Boolean(this.data.internalName !== this.data.name))
      },
      set(val) {
        if (val) this.internalNameState = true
        else {
          this.internalNameState = false
          this.data.internalName = null
        }
      },
    },
  },
  methods: {
    ...mapActions('ResidentialTemplate', ['fetch', 'mutate', 'remove']),
    ...mapMutations('ResidentialTemplate', ['set']),
    submit() {
      if (!this.validator.passes || !this.hasChanges) return Promise.resolve()

      return this.mutate({ id: this.id, ...this.getData({ diff: true }) }).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)

        this.$router.replace({
          name: 'residential-template-edit',
          params: { id: data.id, step: this.$route.params.step || 'presentation' },
        })
      })
    },
    complete() {
      this.submit().then(() => this.$router.push({ name: 'residential-templates' }))
    },
    duplicate() {
      this.duplicatePromise = Api.clone(this.id).then(() => {
        this.$router.push({ name: 'residential-templates' })
      })
    },
    onNavigation(callback) {
      callback()
      this.submit()
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createUnitCatalog') : this.$path('item.internalName'),
    }
  },
  components: {
    HeaderComponent,
    StepperComponent,
    RemoveComponent,
    DropdownComponent,
    SyncComponent,
    Actions,
  },
}
</script>

<style lang="scss">
.residential-template-mutate {
  background-color: $background-grey;

  .elder-stepper__component > * {
    margin: 2rem auto;
  }
}
</style>
